import {DatePicker} from '@github-ui/date-picker'
import type {CloseGesture, SubmitGesture} from '@github-ui/date-picker/types'
import {testIdProps} from '@github-ui/test-id-props'
import {formatISO} from 'date-fns'
import {memo, useCallback, useState} from 'react'

import type {DateValue} from '../../../api/columns/contracts/date'
import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import {ItemValueAdd, ItemValueEdit} from '../../../api/stats/contracts'
import {formatDateString} from '../../../helpers/parsing'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import {useUpdateItem} from '../../../hooks/use-update-item'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {RepoPicker} from '../../repo-picker'
import {DateRenderer} from '../renderers'
import {cellEditorTestId} from '../test-identifiers'
import {useConvertToIssueHelper} from './use-convert-to-issue-helper'
import {useSummaryAnchor} from './use-summary-anchor'

type Props = Readonly<{
  currentValue: ColumnValue<DateValue>
  columnId: number
  rowIndex: number
  replaceContents: boolean
  model: MemexItemModel
  isIssueField: boolean
}>

const createDateValue = (nextValue: Date | null): DateValue | undefined =>
  nextValue ? {value: new Date(formatISO(nextValue, {representation: 'date'}))} : undefined

const ANCHOR_TEST_ID = 'table-cell-date-editor'

export const DateEditor: React.FC<Props> = memo(function DateEditor(props) {
  const {model, rowIndex, columnId, currentValue, isIssueField} = props
  const {updateItem} = useUpdateItem()
  const {postStats} = usePostStats()
  const [isOpen, setIsOpen] = useState(true)
  const dateValue = !props.replaceContents && hasValue(currentValue) ? currentValue.value : null
  const {showConvertToIssue, onRowConvertSuccess} = useConvertToIssueHelper({model, columnId, isIssueField})

  const setClosed = useCallback(() => setIsOpen(false), [])

  const renderButton = useCallback(() => {
    return <DateRenderer currentValue={currentValue} model={model} />
  }, [currentValue, model])

  const {renderAnchor, onClickOutside, onOpenChange} = useSummaryAnchor({
    columnId,
    renderButton,
    anchorTestId: ANCHOR_TEST_ID,
    onSummaryClose: setClosed,
  })

  const closeDatePicker = useCallback(
    (gesture: CloseGesture | SubmitGesture) => {
      onOpenChange(false, gesture)
    },
    [onOpenChange],
  )

  const handleUpdate = useCallback(
    async (newValue: Date | null) => {
      await updateItem(model, {
        dataType: MemexColumnDataType.Date,
        memexProjectColumnId: columnId,
        value: createDateValue(newValue),
        isIssueField,
      })
      postStats({
        name: dateValue ? ItemValueEdit : ItemValueAdd,
        memexProjectColumnId: columnId,
        memexProjectItemId: model.id,
      })
    },
    [columnId, dateValue, model, postStats, updateItem, isIssueField],
  )

  if (showConvertToIssue) {
    return (
      <RepoPicker
        renderAnchor={renderAnchor}
        isOpen
        showPrompt
        item={model}
        onOpenChange={onOpenChange}
        onSuccess={onRowConvertSuccess}
      />
    )
  }

  return (
    <DatePicker
      open={isOpen}
      onChange={handleUpdate}
      onClose={closeDatePicker}
      value={dateValue ? new Date(formatDateString(dateValue.value)) : null}
      showTodayButton={false}
      showClearButton
      anchoredOverlayProps={{
        overlayProps: {
          onEscape: () => closeDatePicker('escape'),
          onClickOutside,
          onMouseDown: e => {
            e.preventDefault()
          },
          ...testIdProps(cellEditorTestId(rowIndex, columnId.toString())),
        },
        focusTrapSettings: {restoreFocusOnCleanUp: false},
      }}
      anchor={renderAnchor}
    />
  )
})

DateEditor.displayName = 'DateEditor'
